/**
 * Nickname and password rules of the game's accounts (auth.signUp / auth.signIn). ONE module, browser-safe (no Node,
 * DOM or protocol imports): the server applies it authoritatively (src/server/core/services/auth.ts) and the game UI
 * uses the same functions for instant feedback while the player types. The reasons are the stable
 * `details.reason` values of the protocol's VALIDATION_FAILED errors (src/shared/protocol/reasons.ts), so a client of
 * any platform shows the same message for the same problem.
 *
 * Nickname
 *  - The input is NFC-normalized and trimmed at both ends (String.prototype.trim: Unicode white space and line
 *    terminators); the stored display nickname keeps the player's case exactly.
 *  - 3..16 characters after trimming.
 *  - Allowed characters: Latin letters A-Z a-z, Cyrillic letters (U+0400..U+045F: Russian, Ukrainian incl. Єє Іі Її,
 *    Belarusian, Serbian...; plus Ґґ U+0490/U+0491), digits 0-9, underscore, hyphen, dot and single inner spaces
 *    (no double spaces; leading and trailing ones are trimmed). At least one letter, and letters of ONE script: Latin
 *    and Cyrillic letters are not mixed in one nickname (a Cyrillic "а" inside "Alice" would impersonate it).
 *  - Everything else is refused (control and format characters such as zero-width ones, emoji and lone UTF-16
 *    surrogates, quotes, apostrophes, <, >, &, slashes, other scripts and symbols).
 *  - Uniqueness and sign-in lookup are case-insensitive: `nicknameKey` (the normalized nickname in lower case). A
 *    nickname whose key equals an existing account's (nickname accounts, the username accounts of auth.register and
 *    the display names of accounts without a nickname share one namespace) or a guest default name (`<prefix>-XXXX`,
 *    isGuestDefaultName) is taken. So is a nickname that LOOKS the same as a taken one: its whole-script lookalike
 *    (`nicknameLookalikes`: "COCA" in Latin letters and "СОСА" in Cyrillic ones) counts as the same name.
 * Password
 *  - Not trimmed or otherwise changed; 8..128 characters (code points); not only white space; no control characters
 *    and no lone UTF-16 surrogates (half of an emoji has no UTF-8 form: the hash would see U+FFFD in its place, so two
 *    different passwords would be one).
 * Sign-in checks only what every existing account satisfies (a nickname is given and not absurdly long, a password is
 * given and not over the limit): any other input simply matches no account (UNAUTHORIZED 'invalid_credentials').
 */

export const NICKNAME_MIN = 3;
export const NICKNAME_MAX = 16;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;
/** Longest nickname auth.signIn looks up: the username accounts of auth.register have up to 32 characters. */
export const SIGN_IN_NICKNAME_MAX = 32;
/**
 * Bounds of the request payload schema: generous, so that the service answers a too long value with its specific
 * reason; anything longer is refused as a malformed payload (BAD_REQUEST 'invalid_payload').
 */
export const NICKNAME_INPUT_MAX = 256;
export const PASSWORD_INPUT_MAX = 1024;

export type NicknameProblem = 'nickname_required' | 'nickname_too_short' | 'nickname_too_long' | 'nickname_invalid';
export type PasswordProblem = 'password_required' | 'password_too_short' | 'password_too_long' | 'password_invalid';
export type CredentialProblemReason = NicknameProblem | PasswordProblem;

/** Every reason of this module (the protocol's VALIDATION_FAILED reasons of the credential fields). */
export const CREDENTIAL_PROBLEMS: readonly CredentialProblemReason[] = [
  'nickname_required',
  'nickname_too_short',
  'nickname_too_long',
  'nickname_invalid',
  'password_required',
  'password_too_short',
  'password_too_long',
  'password_invalid',
];

/** A refused credential field: `details` of VALIDATION_FAILED { reason, field }. */
export interface CredentialProblem {
  field: 'nickname' | 'password';
  reason: CredentialProblemReason;
}

export type NicknameCheck = { ok: true; nickname: string; key: string } | { ok: false; field: 'nickname'; reason: NicknameProblem };
export type PasswordCheck = { ok: true } | { ok: false; field: 'password'; reason: PasswordProblem };

const LETTER_CLASS = 'A-Za-z\\u0400-\\u045F\\u0490\\u0491';
const WORD_CLASS = `${LETTER_CLASS}0-9_.\\-`;
/** Allowed characters, with single spaces only between them (the input is already trimmed). */
const NICKNAME_PATTERN = new RegExp(`^[${WORD_CLASS}]+(?: [${WORD_CLASS}]+)*$`);
const LETTER = new RegExp(`[${LETTER_CLASS}]`);
const LATIN_LETTER = /[A-Za-z]/;
const CYRILLIC_LETTER = /[\u0400-\u045F\u0490\u0491]/;
/** C0 / C1 control characters and DEL. */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;
/** A lone UTF-16 surrogate (with the u flag a surrogate pair is one code point, so only an unpaired half matches). */
const LONE_SURROGATE = /\p{Cs}/u;

function codePoints(s: string): number {
  let n = 0;
  for (const _ of s) n++;
  return n;
}

/** The nickname as it is checked and stored: NFC-normalized and trimmed at both ends (case kept). */
export function normalizeNickname(raw: string): string {
  return String(raw).normalize('NFC').trim();
}

/** The case-insensitive key of a nickname (uniqueness, sign-in lookup): normalizeNickname in lower case. */
export function nicknameKey(raw: string): string {
  return normalizeNickname(raw).toLowerCase();
}

/** Checks a nickname for sign-up (see the module comment); on success the display form and the key. */
export function checkNickname(raw: unknown): NicknameCheck {
  if (typeof raw !== 'string') return { ok: false, field: 'nickname', reason: 'nickname_required' };
  const nickname = normalizeNickname(raw);
  if (nickname === '') return { ok: false, field: 'nickname', reason: 'nickname_required' };
  if (!NICKNAME_PATTERN.test(nickname)) return { ok: false, field: 'nickname', reason: 'nickname_invalid' };
  const length = codePoints(nickname);
  if (length < NICKNAME_MIN) return { ok: false, field: 'nickname', reason: 'nickname_too_short' };
  if (length > NICKNAME_MAX) return { ok: false, field: 'nickname', reason: 'nickname_too_long' };
  if (!LETTER.test(nickname)) return { ok: false, field: 'nickname', reason: 'nickname_invalid' };
  if (LATIN_LETTER.test(nickname) && CYRILLIC_LETTER.test(nickname)) return { ok: false, field: 'nickname', reason: 'nickname_invalid' };
  return { ok: true, nickname, key: nickname.toLowerCase() };
}

/**
 * Lower-case Latin letters and the Cyrillic letters that look the same in upper or lower case (a/\u0430, B/\u0412,
 * c/\u0441, e/\u0435, H/\u041D, i/\u0456, j/\u0458, K/\u041A, M/\u041C, o/\u043E, p/\u0440, s/\u0455,
 * T/\u0422, x/\u0445, y/\u0443); written as escapes. Lookalikes inside one script (l / I / 1, O / 0) are not covered.
 */
const LOOKALIKES: readonly (readonly [string, string])[] = [
  ['a', '\u0430'],
  ['b', '\u0432'],
  ['c', '\u0441'],
  ['e', '\u0435'],
  ['h', '\u043D'],
  ['i', '\u0456'],
  ['j', '\u0458'],
  ['k', '\u043A'],
  ['m', '\u043C'],
  ['o', '\u043E'],
  ['p', '\u0440'],
  ['s', '\u0455'],
  ['t', '\u0442'],
  ['x', '\u0445'],
  ['y', '\u0443'],
];
const LATIN_TO_CYRILLIC = new Map(LOOKALIKES);
const CYRILLIC_TO_LATIN = new Map(LOOKALIKES.map(([l, c]) => [c, l] as const));

/**
 * The other keys a nickname key looks like: its letter-by-letter transliteration into the other script ("coca" ->
 * the Cyrillic "\u0441\u043E\u0441\u0430") when every letter has a lookalike there; [] otherwise (a nickname of one script
 * can only look like a nickname of the other one: mixing the scripts is refused). Uniqueness checks the key and these.
 */
export function nicknameLookalikes(key: string): string[] {
  const chars = Array.from(key);
  const latin = chars.some((c) => LATIN_LETTER.test(c));
  const cyrillic = chars.some((c) => CYRILLIC_LETTER.test(c));
  if (latin === cyrillic) return [];
  const map = latin ? LATIN_TO_CYRILLIC : CYRILLIC_TO_LATIN;
  const script = latin ? LATIN_LETTER : CYRILLIC_LETTER;
  let out = '';
  for (const c of chars) {
    if (!script.test(c)) out += c;
    else {
      const twin = map.get(c);
      if (twin === undefined) return [];
      out += twin;
    }
  }
  return [out];
}

/**
 * One spelling for a key and its lookalikes (the Cyrillic lookalike letters replaced by their Latin twins): the
 * server's lock of a name (a sign-up and a rename to lookalike names never run at the same time).
 */
export function nicknameSkeleton(key: string): string {
  let out = '';
  for (const c of key) out += CYRILLIC_TO_LATIN.get(c) ?? c;
  return out;
}

/** Checks a new password (see the module comment). The password is never trimmed or changed. */
export function checkPassword(raw: unknown): PasswordCheck {
  if (typeof raw !== 'string' || raw === '') return { ok: false, field: 'password', reason: 'password_required' };
  const length = codePoints(raw);
  if (length > PASSWORD_MAX) return { ok: false, field: 'password', reason: 'password_too_long' };
  if (length < PASSWORD_MIN) return { ok: false, field: 'password', reason: 'password_too_short' };
  if (CONTROL.test(raw) || LONE_SURROGATE.test(raw) || raw.trim() === '') return { ok: false, field: 'password', reason: 'password_invalid' };
  return { ok: true };
}

/** The first problem of a sign-up form (nickname first), or null when both fields are acceptable. */
export function checkSignUp(nickname: unknown, password: unknown): CredentialProblem | null {
  const n = checkNickname(nickname);
  if (!n.ok) return { field: n.field, reason: n.reason };
  const p = checkPassword(password);
  if (!p.ok) return { field: p.field, reason: p.reason };
  return null;
}

/**
 * The first problem of a sign-in form, or null. Only what every existing account satisfies is checked (a missing or
 * over-long value); a nickname or password that breaks the sign-up rules otherwise just matches no account.
 */
export function checkSignIn(nickname: unknown, password: unknown): CredentialProblem | null {
  const n = typeof nickname === 'string' ? normalizeNickname(nickname) : '';
  if (n === '') return { field: 'nickname', reason: 'nickname_required' };
  if (codePoints(n) > SIGN_IN_NICKNAME_MAX) return { field: 'nickname', reason: 'nickname_too_long' };
  if (typeof password !== 'string' || password === '') return { field: 'password', reason: 'password_required' };
  if (codePoints(password) > PASSWORD_MAX) return { field: 'password', reason: 'password_too_long' };
  return null;
}

/**
 * Whether a nickname key has the form of a guest default name (`<prefix>-XXXX`: 4..6 letters or digits after the
 * prefix, the names auth.guest hands out, e.g. 'Игрок-7K2Q'): such names stay reserved for guests.
 */
export function isGuestDefaultName(key: string, prefix: string): boolean {
  const p = `${prefix.normalize('NFC').toLowerCase()}-`;
  return key.startsWith(p) && /^[0-9a-z]{4,6}$/.test(key.slice(p.length));
}
